import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { LANGUAGE_NATIVE_NAMES, type Language } from '@lightbm/shared';
import { BrandMark } from '@/components/layout/BrandMark';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import i18n, { hasStoredLanguage, setLanguage } from '@/i18n';
import { queryKeys } from '@/lib/query-client';
import { authApi } from '../api';

export function usePublicContext() {
  return useQuery({
    queryKey: queryKeys.publicContext,
    queryFn: authApi.publicContext,
    staleTime: 5 * 60_000,
  });
}

const FEATURES = ['recorded', 'locations', 'alerts'] as const;

/** Split screen of the "Sign in" design: form on the left, promise of the product on the right. */
export function AuthLayout() {
  const { t } = useTranslation('auth');
  const { data: context } = usePublicContext();
  const languages = context?.enabledLanguages ?? (['en', 'fr'] as Language[]);

  // First visit on this device: speak the company's default language.
  useEffect(() => {
    if (context && !hasStoredLanguage()) void i18n.changeLanguage(context.defaultLanguage);
  }, [context]);

  return (
    <div className="grid min-h-dvh bg-ground lg:grid-cols-2">
      <div className="flex justify-center px-5 py-10 sm:px-10 lg:justify-start lg:px-[10%] lg:py-[12vh]">
        <div className="w-full max-w-[584px]">
          <div className="flex items-center gap-3.5">
            <BrandMark logoUrl={context?.logoUrl} className="size-[60px] rounded-[14px]" />
            <div className="min-w-0">
              <p className="text-[21px] leading-tight font-semibold">Light Business Management</p>
              <p className="truncate text-ink-2">{context?.companyName}</p>
            </div>
          </div>

          {languages.length > 1 && (
            <SegmentedControl
              className="mt-8 min-w-80"
              size="lg"
              label={t('common:language.label')}
              value={(i18n.language as Language) ?? 'en'}
              onChange={(language) => void setLanguage(language)}
              options={languages.map((language) => ({
                value: language,
                label: LANGUAGE_NATIVE_NAMES[language],
                lang: language,
              }))}
            />
          )}

          <div className="mt-10">
            <Outlet />
          </div>
        </div>
      </div>

      <aside className="hidden flex-col justify-center bg-primary-deep px-[8%] text-white lg:flex">
        <h2 className="max-w-xl text-[44px] leading-[1.15] font-semibold tracking-tight">
          {t('promise.title')}
        </h2>
        <ul className="mt-10 space-y-7">
          {FEATURES.map((feature) => (
            <li key={feature} className="flex gap-4">
              <span className="flex size-12 shrink-0 items-center justify-center rounded-control bg-white/10">
                <Check className="size-5" />
              </span>
              <span>
                <span className="block text-[19px] font-semibold">
                  {t(`promise.${feature}.title`)}
                </span>
                <span className="mt-1 block text-[17px] text-white/75">
                  {t(`promise.${feature}.text`)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
